import { Money, isValidMoney, parseMoney } from '@betterment/core';
import * as Yup from 'yup';

import { OrderUnit, toAmountChoice } from './types';

export interface SellOrderFormValues {
  assetTicker: string;
  coordinatedAccountId: string;
  amount: Money | number | string;
  heldAmount: Money | number;
  unit: OrderUnit;
  sellAll?: boolean;
}

const cleanString = (value: string): string => value.replace(/[^\d.]/g, '');
export const transformShares = (value: string): number | null => {
  if (!value) return null;
  const shares = Number(value);
  return Number.isNaN(shares) ? null : shares;
};

const transformAmount = (amount: Money | number | string, unit: OrderUnit): Money | number | null => {
  if (typeof amount !== 'string') {
    return amount;
  }

  const cleaned = cleanString(amount);
  return unit === OrderUnit.Dollars ? parseMoney(cleaned) : transformShares(cleaned);
};

function withinValidationContext(obj: unknown): Partial<SellOrderFormValues> {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('Expected parent to be an object');
  }
  return obj;
}

function toTypedAmountChoice(context: Yup.TestContext, amount: Money | number | undefined) {
  const { unit } = withinValidationContext(context.parent);
  if (!unit) {
    throw new Error('Unit is required');
  }
  if (amount === undefined) {
    throw new Error('Amount is required');
  }
  return toAmountChoice(unit, amount);
}

function testAmountType(this: Yup.TestContext, amount: Money | number | undefined): boolean | Yup.ValidationError {
  const { unit } = withinValidationContext(this.parent);

  if (unit === OrderUnit.Dollars) {
    return isValidMoney(amount) || this.createError({ message: 'Amount must be Money when unit is dollars' });
  }

  if (unit === OrderUnit.Shares) {
    return typeof amount === 'number' || this.createError({ message: 'Amount must be a number when unit is shares' });
  }

  return true;
}

function testOverMinAmount(
  this: Yup.TestContext,
  untypedAmount: Money | number | undefined
): boolean | Yup.ValidationError {
  const { unit, amount } = toTypedAmountChoice(this, untypedAmount);

  if (unit === OrderUnit.Dollars) {
    return amount.cents >= 100 || this.createError({ message: 'Amount must be at least $1' });
  }

  return amount > 0 || this.createError({ message: 'Invalid amount' });
}

function testUnderMaxAmount(
  this: Yup.TestContext,
  untypedAmount: Money | number | undefined
): boolean | Yup.ValidationError {
  const { heldAmount } = withinValidationContext(this.parent);

  const { unit, amount } = toTypedAmountChoice(this, untypedAmount);

  if (unit === OrderUnit.Dollars && isValidMoney(heldAmount)) {
    return amount.cents <= heldAmount.cents || this.createError({ message: 'Exceeds available amount' });
  }

  if (unit === OrderUnit.Shares && typeof heldAmount === 'number') {
    return amount <= heldAmount || this.createError({ message: 'Exceeds available amount' });
  }

  return true;
}

function testZeroHeldAmount(this: Yup.TestContext): boolean | Yup.ValidationError {
  const { heldAmount } = withinValidationContext(this.parent);

  if (heldAmount !== undefined && (typeof heldAmount === 'number' ? heldAmount <= 0 : heldAmount.cents <= 0)) {
    return this.createError({ message: 'Exceeds available amount' });
  }

  return true;
}

export const ValidationSchema = Yup.object().shape({
  amount: Yup.mixed<Money | number>()
    .when('unit', ([unit]: OrderUnit[], schema) =>
      schema.transform((value: string | Money | number) => transformAmount(value, unit))
    )
    .when('sellAll', {
      is: true,
      then: amount => amount.nullable(),
      otherwise: amount =>
        amount
          .required('Amount is required')
          .test('amount-type', testAmountType)
          .test('max-amount', testUnderMaxAmount)
          .test('minimum-amount', testOverMinAmount)
          .test('zero-held-amount', testZeroHeldAmount),
    }),
  unit: Yup.string().oneOf([OrderUnit.Dollars, OrderUnit.Shares]).required(),
  assetTicker: Yup.string().required(),
  coordinatedAccountId: Yup.string().required('Coordinated account is required'),
  sellAll: Yup.boolean().required().default(false),
});
